/**
 * Lightweight canvas-based confetti burst.
 * No external dependencies — creates a temporary canvas overlay, fires
 * colourful particles, then removes itself once the animation completes.
 */

interface Particle {
  x: number; y: number; vx: number; vy: number;
  size: number; color: string; gravity: number; drag: number;
  alpha: number; decay: number; rotation: number; rotSpeed: number;
}

/** Viewport coordinates captured before an async action replaces its button. */
export interface ConfettiOrigin { x: number; y: number; }

const COLORS = [
  '#22c55e', '#4ade80', '#86efac',
  '#facc15', '#fbbf24', '#fde047',
  '#38bdf8', '#818cf8', '#f472b6',
];

function makeParticle(cx: number, cy: number): Particle {
  const angle = Math.random() * Math.PI * 2;
  const speed = 4 + Math.random() * 8;

  return {
    x: cx, y: cy,
    vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed - 4,
    size: 4 + Math.random() * 4,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    gravity: 0.12, drag: 0.98, alpha: 1,
    decay: 0.012 + Math.random() * 0.008,
    rotation: Math.random() * 360, rotSpeed: (Math.random() - 0.5) * 12,
  };
}

function stepParticle(p: Particle): void {
  p.vy += p.gravity; p.vx *= p.drag; p.vy *= p.drag;
  p.x += p.vx; p.y += p.vy; p.alpha -= p.decay; p.rotation += p.rotSpeed;
}

function drawParticle(ctx: CanvasRenderingContext2D, p: Particle): void {
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate((p.rotation * Math.PI) / 180);
  ctx.globalAlpha = Math.max(0, p.alpha);
  ctx.fillStyle = p.color;
  ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
  ctx.restore();
}

function createCanvas(): HTMLCanvasElement {
  const c = document.createElement('canvas');

  c.style.cssText = 'position:fixed;top:0;left:0;width:100vw;height:100vh;pointer-events:none;z-index:99999';
  c.width = window.innerWidth;
  c.height = window.innerHeight;
  document.body.appendChild(c);

  return c;
}

function resolveOrigin(canvas: HTMLCanvasElement, target?: HTMLElement | ConfettiOrigin): [number, number] {
  if (!target) return [canvas.width / 2, canvas.height / 2];
  if ('x' in target && 'y' in target) return [target.x, target.y];

  const r = target.getBoundingClientRect();

  return [r.left + r.width / 2, r.top + r.height / 2];
}

function animate(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, particles: Particle[]): void {
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const alive = particles.filter((p) => p.alpha > 0);

  alive.forEach((p) => { stepParticle(p); drawParticle(ctx, p); });

  if (alive.length > 0) {
    window.requestAnimationFrame(() => animate(ctx, canvas, particles));
  } else {
    canvas.remove();
  }
}

/* ── Read tracking for confetti dedup ─────────────────────────────── */

const _readIds = new Set<string>();
let _readLoaded = false;
let _projectId = '';
let _pendingSave: ReturnType<typeof setTimeout> | null = null;

function selectReadProject(projectId: string): void {
  if (_projectId !== projectId) _readIds.clear();
  _projectId = projectId;
}

/** Hydrate confetti state embedded in the AI overview bootstrap response. */
export function hydrateReadIds(projectId: string, messageIds: string[]): void {
  selectReadProject(projectId);
  messageIds.forEach((id) => _readIds.add(id));
  _readLoaded = true;
}

/** Load read message IDs from the backend. Call once on panel mount. */
export async function loadReadIds(projectId: string): Promise<void> {
  if (_readLoaded && _projectId === projectId) return;
  selectReadProject(projectId);

  try {
    // Dynamic import to avoid circular dependency
    const { projectService } = await import('../../services/projectService');
    const ids = await projectService.getReadMessageIds(projectId);

    if (_projectId === projectId) hydrateReadIds(projectId, ids);
  } catch { /* silent */ }
  if (_projectId === projectId) _readLoaded = true;
}

/** Check if a message has already shown confetti. */
export function isMessageRead(messageId: string): boolean {
  return _readIds.has(messageId);
}

/** Mark a message as read (confetti shown). Debounced save to backend. */
export function markMessageRead(messageId: string): void {
  _readIds.add(messageId);

  // Debounce: batch saves to avoid spamming the API
  if (_pendingSave) clearTimeout(_pendingSave);
  _pendingSave = setTimeout(async () => {
    if (!_projectId) return;

    try {
      const { projectService } = await import('../../services/projectService');

      await projectService.markMessagesRead(_projectId, Array.from(_readIds));
    } catch { /* silent */ }
  }, 2000);
}

/** Fire confetti only if this message hasn't been read yet. */
export function fireConfettiOnce(messageId: string, target?: HTMLElement): boolean {
  if (isMessageRead(messageId)) return false;

  markMessageRead(messageId);
  fireConfetti(target);

  return true;
}

export function fireConfetti(target?: HTMLElement | ConfettiOrigin): void {
  const canvas = createCanvas();
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    canvas.remove();

    return;
  }

  const [cx, cy] = resolveOrigin(canvas, target);

  window.requestAnimationFrame(() => animate(
    ctx, canvas, Array.from({ length: 80 }, () => makeParticle(cx, cy)),
  ));
}
